<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const faq = [
  {
    q: 'Apa beda "Laba Kotor" dan "Laba Bersih" di laporan ini?',
    a: 'Laba Kotor = Revenue dikurangi Diskon dan HPP (biaya bahan baku). Laba Bersih = Laba Kotor dikurangi semua Pengeluaran operasional (listrik, gaji, sewa, dll). Laba Bersih adalah angka final untung/rugi toko Anda.',
  },
  {
    q: 'Kenapa banner di atas berwarna merah dan bertuliskan "Rugi Bersih"?',
    a: 'Itu artinya total Pengeluaran + HPP + Diskon lebih besar dari Revenue pada periode yang Anda pilih. Coba cek "Breakdown Pengeluaran" untuk lihat kategori mana yang paling besar, lalu evaluasi apakah ada yang bisa dihemat.',
  },
  {
    q: 'Dari mana angka "HPP (dari resep)" didapat?',
    a: 'Sistem menghitung HPP otomatis berdasarkan resep (recipe) yang Anda atur di setiap produk — yaitu bahan baku apa saja dan berapa banyak yang dipakai per produk, dikalikan dengan harga beli bahan tersebut, lalu dijumlahkan sesuai jumlah produk yang terjual.',
  },
  {
    q: 'Apa itu "Pajak dipungut" di bagian Pendapatan?',
    a: 'Ini adalah total pajak (misalnya PPN atau pajak restoran) yang Anda kumpulkan dari pelanggan saat transaksi. Angka ini bersifat informasi saja dan tidak mengurangi Laba Kotor, karena pajak biasanya harus disetorkan terpisah, bukan bagian dari keuntungan toko.',
  },
  {
    q: 'Kategori pengeluaran saya tidak muncul di "Breakdown Pengeluaran", kenapa?',
    a: 'Kemungkinan belum ada transaksi pengeluaran yang dicatat untuk kategori tersebut pada periode yang dipilih. Cek kembali di menu Keuangan → Pengeluaran, pastikan tanggal pengeluaran sesuai dengan periode laporan.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/laporan" class="text-decoration-none text-medium-emphasis text-body-2">
            Laporan
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Laporan Laba Rugi</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="warning" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-file-chart-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Laporan Laba Rugi</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Profit & Loss Statement — untung atau rugi?
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong> Menjawab pertanyaan paling penting
          bagi setiap pemilik usaha — "toko saya untung atau rugi bulan ini?" —
          lengkap dengan rinciannya dari mana asalnya.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="warning" />
            Apa itu Laporan Laba Rugi?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Laba Rugi (Profit & Loss)</strong> adalah laporan yang
              menjawab satu pertanyaan besar: <em>"Setelah semua uang masuk dan
              keluar, saya untung berapa — atau malah rugi?"</em>
            </p>
            <p class="text-body-2 mb-3">
              Bayangkan seperti ini: Anda jualan Es Kopi Susu seharga
              <strong>Rp 20.000</strong>. Biaya bahan (kopi, susu, gula, cup)
              sekitar <strong>Rp 8.000</strong>. Itu baru "Laba Kotor" per cup.
              Tapi Anda juga bayar listrik, sewa tempat, gaji karyawan setiap
              bulan — itu yang disebut <strong>Pengeluaran Operasional</strong>.
              Setelah dikurangi semua itu, sisanya adalah <strong>Laba Bersih</strong> —
              uang yang benar-benar jadi untung Anda.
            </p>
            <VAlert type="success" variant="tonal" class="mb-0">
              Laporan ini menghitung semuanya otomatis, dari data penjualan,
              resep produk, hingga pengeluaran yang sudah Anda catat.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Penggunaan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-cursor-line" class="me-2" color="success" />
            Cara Menggunakan
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Buka menu <strong>Laporan → Laba Rugi</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                Isi <strong>"Dari Tanggal"</strong> dan <strong>"Sampai Tanggal"</strong>
                — misalnya untuk lihat laba rugi bulan ini: 01/07/2026 s.d. 31/07/2026
              </li>
              <li class="mb-3">
                Klik tombol <strong>"Tampilkan"</strong>
              </li>
              <li class="mb-3">
                Lihat banner besar di atas — hijau berarti untung, merah berarti rugi
              </li>
              <li class="mb-0">
                Klik <strong>"Excel"</strong> atau <strong>"PDF"</strong> jika ingin
                menyimpan laporan sebagai arsip atau untuk dilaporkan ke pihak lain
                (misalnya akuntan atau investor)
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Income Statement Detail -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-file-list-3-line" class="me-2" color="info" />
            Alur Perhitungan "Income Statement"
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Laporan disusun dari atas ke bawah seperti anak tangga — setiap
              baris mengurangi angka di atasnya:
            </p>
            <VTable>
              <thead>
                <tr>
                  <th>Baris</th>
                  <th>Penjelasan</th>
                  <th>Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Revenue (Omzet)</strong></td>
                  <td class="text-body-2 py-2">Total penjualan kotor sebelum dikurangi apa pun</td>
                  <td class="text-body-2 py-2">Rp 45.000.000</td>
                </tr>
                <tr>
                  <td>Diskon diberikan</td>
                  <td class="text-body-2 py-2">Total potongan harga ke pelanggan</td>
                  <td class="text-body-2 py-2 text-warning">(Rp 1.200.000)</td>
                </tr>
                <tr>
                  <td>Pajak dipungut</td>
                  <td class="text-body-2 py-2">Informasi pajak yang dikumpulkan dari transaksi (tidak mengurangi laba)</td>
                  <td class="text-body-2 py-2">Rp 900.000</td>
                </tr>
                <tr>
                  <td>HPP (dari resep)</td>
                  <td class="text-body-2 py-2">Biaya bahan baku dari semua produk terjual</td>
                  <td class="text-body-2 py-2 text-error">(Rp 18.000.000)</td>
                </tr>
                <tr>
                  <td><strong>= LABA KOTOR</strong></td>
                  <td class="text-body-2 py-2">Revenue − Diskon − HPP, plus persentase margin</td>
                  <td class="text-body-2 py-2 text-info font-weight-bold">Rp 25.800.000 (57.3%)</td>
                </tr>
                <tr>
                  <td>Pengeluaran (per kategori)</td>
                  <td class="text-body-2 py-2">Rincian biaya operasional per kategori (listrik, gaji, sewa, dll)</td>
                  <td class="text-body-2 py-2 text-error">(Rp 12.000.000)</td>
                </tr>
                <tr>
                  <td><strong>= LABA / RUGI BERSIH</strong></td>
                  <td class="text-body-2 py-2">Laba Kotor − Total Pengeluaran, plus persentase net margin</td>
                  <td class="text-body-2 py-2 text-success font-weight-bold">Rp 13.800.000 (30.7%)</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Breakdown & Ringkasan -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-pie-chart-line" class="me-2" color="primary" />
            "Breakdown Pengeluaran"
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-0">
              Blok ini menampilkan setiap kategori pengeluaran dalam bentuk
              progress bar berwarna, lengkap dengan persentase kontribusinya
              terhadap total pengeluaran. Berguna untuk cepat melihat kategori
              mana yang paling "menggerogoti" keuntungan Anda — misalnya jika
              "Sewa" ternyata memakan 60% dari semua pengeluaran.
            </p>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-bar-chart-2-line" class="me-2" color="primary" />
            Blok "Ringkasan"
          </VCardTitle>
          <VCardText>
            <ul class="text-body-2 ps-4 mb-0">
              <li class="mb-1"><strong>Total Transaksi</strong> — jumlah order pada periode ini</li>
              <li class="mb-1"><strong>Rata-rata per Transaksi</strong> — Revenue dibagi jumlah transaksi</li>
              <li class="mb-1"><strong>Gross Margin</strong> — persentase Laba Kotor terhadap Revenue</li>
              <li class="mb-0"><strong>Net Margin</strong> — persentase Laba Bersih terhadap Revenue</li>
            </ul>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Catatan Penting -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert type="warning" variant="tonal" icon="ri-alert-line">
          <strong>Catatan Penting:</strong> Laporan Laba Rugi hanya menghitung
          Pengeluaran yang sudah Anda catat di menu <strong>Keuangan → Pengeluaran</strong>.
          Jika ada biaya yang belum dicatat (misalnya bayar listrik bulan ini
          belum diinput), Laba Bersih yang ditampilkan akan lebih besar dari
          kondisi sebenarnya. Pastikan semua pengeluaran sudah tercatat sebelum
          menyimpulkan angka laporan ini.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Cek laporan ini setiap akhir bulan</strong>, bukan
                  setiap hari. Laba Rugi lebih bermakna dilihat dalam periode
                  yang cukup panjang (bulanan), bukan harian.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Catat semua pengeluaran secepatnya</strong> di menu
                  Keuangan agar laporan Laba Rugi selalu akurat dan tidak
                  "kelihatan untung besar" padahal ada tagihan yang belum diinput.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Bandingkan Net Margin antar bulan.</strong> Jika terus
                  menurun meski omzet naik, kemungkinan ada pengeluaran yang
                  tumbuh lebih cepat dari pendapatan — saatnya evaluasi biaya.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Laporan Penjualan', sub: 'Detail transaksi per periode', icon: 'ri-line-chart-line', color: 'success', to: '/userguide/laporan/penjualan' },
              { title: 'Pengeluaran', sub: 'Catat biaya operasional toko', icon: 'ri-money-dollar-circle-line', color: 'error', to: '/userguide/keuangan/pengeluaran' },
              { title: 'Dashboard', sub: 'Ringkasan performa harian', icon: 'ri-dashboard-line', color: 'primary', to: '/userguide/laporan/dashboard' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
